import { useState, useEffect, useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { fetchStatements } from "../queries"
import { Heading } from "@planningcenter/doxy-web"
import { AlertMessage, Loading } from "source/shared/components"
import { Icon } from "source/shared/components"
import { StatementDetails } from "./components/StatementDetails"
import { css } from "@emotion/react"
import { getAllStatementYears } from "../utils/getAllStatementYears"
import { StatementYearFilter } from "./components/StatementYearFilter"

export default function Statements() {
  const {
    data: statements = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["Statements"],
    queryFn: fetchStatements,
  })

  const [activeYear, setActiveYear] = useState<number | null>(null)

  const statementYears = useMemo(() => {
    return getAllStatementYears(statements)
  }, [statements])

  useEffect(() => {
    if (statementYears.length > 0 && activeYear === null) {
      setActiveYear(statementYears[0])
    }
  }, [statementYears, activeYear])

  const filteredStatements = useMemo(() => {
    if (!activeYear) return []
    return statements.filter(
      (statement) =>
        new Date(statement.created_at).getFullYear() === activeYear,
    )
  }, [statements, activeYear])

  const handleYearChange = (year: number) => {
    setActiveYear(year)
  }

  return (
    <section>
      <div className="mb-2">
        <Heading level={1} size={2} text="Statements" />
      </div>
      {isError ? (
        <AlertMessage type="error" icon="general#exclamation-triangle">
          Something went wrong. Please try again.
        </AlertMessage>
      ) : isLoading ? (
        <Loading message="Loading" />
      ) : (
        statements &&
        (statements.length === 0 ? (
          <div className="ta-c c-tint3 p-4">
            <Icon
              symbol="general#thick-to-bottom-arrow"
              css={css`
                width: 34px;
                height: 34px;
              `}
              className="mb-1"
              title="arrow pointing down"
            />
            <p>No statements available to download</p>
          </div>
        ) : (
          <div className="d-f fd-c g-2">
            <StatementYearFilter
              statementYears={statementYears}
              activeStatementYear={activeYear}
              onChange={handleYearChange}
            />
            {filteredStatements.map((statement) => (
              <StatementDetails statement={statement} key={statement.id} />
            ))}
          </div>
        ))
      )}
    </section>
  )
}
